import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  LayoutGrid,
  List,
  Search,
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import { getOrganizationBySerach } from "../../../Apis/Organization-apis";
import { setClientPreferredOrganizationId } from "../../../Utils/clientActiveOrganization";
import { canSwitchOrganization } from "../../../Utils/organizationPermissions";
import Layout4 from "../../DataLayouts/Layout4";

function orgInitials(short, name) {
  const src = String(short || name || "").trim();
  if (!src) return "OR";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  }
  return src.slice(0, 2).toUpperCase();
}

function n(val) {
  const x = Number(val);
  return Number.isFinite(x) ? x : 0;
}

function pgList(page, pages) {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  if (page <= 4) return [1, 2, 3, 4, 5, "…", pages];
  if (page >= pages - 3) {
    return [1, "…", pages - 4, pages - 3, pages - 2, pages - 1, pages];
  }
  return [1, "…", page - 1, page, page + 1, "…", pages];
}

function OrgStatusToggle({ active, disabled, onToggle }) {
  return (
    <button
      type="button"
      className={`ems-sw${active ? " on" : ""}`}
      disabled={disabled}
      aria-pressed={active}
      title={active ? "Active organization" : "Set active"}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onToggle();
      }}
    />
  );
}

function OrganizationAddInList() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rpp, setRpp] = useState(25);
  const [view, setView] = useState("list");
  const { userData, login } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const canSwitchOrg = canSwitchOrganization(Permission);
  const canEdit = Permission?.includes("ORGANIZATION_EDIT");
  const activeOrgId = userData?.organization?.organization_id;
  const clientId = userData?.organization?.client_id ?? userData?.client_id;

  const switchActiveOrg = async (id) => {
    if (!canSwitchOrg) return;
    try {
      const response = await axios.post(
        `${MAIN_URL}/api/organizations/extra/active`,
        {
          organization_id: id,
          application_user_id: userData.application_user_id,
        }
      );

      const { user } = response.data;
      const dd = user.organization.find(
        (item) =>
          item.organization_id ==
          user.user_active_organization.organization_id
      );

      setClientPreferredOrganizationId(id);

      login({
        ...user,
        organizations: Array.isArray(user?.organization) ? user.organization : [],
        organization: dd,
      });
      toast.success("Status updated successfully!");
    } catch (error) {
      console.error("Switch org failed:", error);
      toast.error(error?.response?.data?.error || "Failed to switch organization");
    }
  };

  useEffect(() => {
    if (!clientId) return;

    setLoading(true);
    getOrganizationBySerach(clientId, searchTerm)
      .then((data) => {
        const rows = data?.organizations?.data || [];
        setOrganizations(
          rows.map((item) => ({
            ...item,
            id: item.organization_id,
          }))
        );
        setPage(1);
      })
      .catch(() => {
        setOrganizations([]);
      })
      .finally(() => setLoading(false));
  }, [clientId, searchTerm]);

  const filtered = useMemo(() => {
    const q = String(searchTerm || "").trim().toLowerCase();
    if (!q) return organizations;
    return organizations.filter((o) => {
      const hay = [
        o.organization_name,
        o.organization_short_name,
        o.business_ownership_type_category_name,
        o.ownership_type_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [organizations, searchTerm]);

  const pages = Math.max(1, Math.ceil(filtered.length / rpp));
  const curPage = Math.min(page, pages);
  const slice = filtered.slice((curPage - 1) * rpp, curPage * rpp);
  const isSingleOrg = organizations.length === 1;

  const getOrgType = (item) =>
    item?.business_ownership_type_category_name ||
    item?.ownership_type_name ||
    item?.organization_ownership_type_name ||
    "—";

  const getEntityCount = (item) => {
    if (Array.isArray(item?.organization_entities)) {
      return item.organization_entities.length;
    }
    const raw = item?.entities ?? item?.entity_count ?? item?.entities_count;
    return n(raw);
  };

  return (
    <div className="ems-dashboard ems-fade-up" id="ems-org-list-print">
      <Layout4
        heading="Organizations"
        subtitle={`Organization Setup • ${organizations.length} organization${organizations.length === 1 ? "" : "s"}`}
        Route="/organization/details"
        btnName="Add Organization"
        add_action="ORGANIZATION_ADD"
      />
      <div className="ems-section-divider" />

      <div className="ems-card ems-dt-toolbar">
        <div className="ems-dt-search">
          <Search size={16} />
          <input
            type="search"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="ems-dt-tools">
          <div className="ems-seg icons">
            <button
              type="button"
              className={view === "list" ? "active" : ""}
              onClick={() => setView("list")}
              aria-label="List view"
            >
              <List size={16} />
            </button>
            <button
              type="button"
              className={view === "grid" ? "active" : ""}
              onClick={() => setView("grid")}
              aria-label="Grid view"
            >
              <LayoutGrid size={16} />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="ems-loading-screen">Loading…</div>
      ) : view === "grid" ? (
        <div className="ems-dt-grid">
          {slice.map((o) => {
            const isActive = String(o.organization_id) === String(activeOrgId);
            const toggleDisabled = isSingleOrg || !canSwitchOrg;
            return (
              <div className="ems-dt-card" key={o.organization_id}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <div className="ems-act-cell">
                    <span className="ems-org-mark">
                      {orgInitials(o.organization_short_name, o.organization_name)}
                    </span>
                    <div>
                      <div className="ems-lead-name">{o.organization_name}</div>
                      <div className="ems-org-type">{o.organization_short_name}</div>
                    </div>
                  </div>
                  <OrgStatusToggle
                    active={isActive}
                    disabled={toggleDisabled}
                    onToggle={() => switchActiveOrg(o.organization_id)}
                  />
                </div>
                <div style={{ marginTop: 12, fontSize: 13 }}>
                  <div>
                    <span className="ems-org-type">Type: </span>
                    {getOrgType(o)}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <span className="ems-org-type">Entities: </span>
                    <span className="ems-batch-stu">{getEntityCount(o)}</span>
                  </div>
                </div>
                {canEdit ? (
                  <button
                    type="button"
                    className="ems-link-edit"
                    style={{ marginTop: 14 }}
                    onClick={() =>
                      navigate(`/organization/details/edit/${o.organization_id}`)
                    }
                  >
                    Edit
                  </button>
                ) : null}
              </div>
            );
          })}
          {!slice.length && (
            <div className="ems-col-empty" style={{ gridColumn: "1 / -1", padding: 44 }}>
              No organizations match your search.
            </div>
          )}
        </div>
      ) : (
        <div className="ems-card ems-table-card">
          <div className="ems-table-wrap">
            <table className="ems-data ems-org-table">
              <thead>
                <tr>
                  <th>Organization Name</th>
                  <th>Short Name</th>
                  <th>Type</th>
                  <th className="ems-num" style={{ width: 96 }}>
                    Entities
                  </th>
                  <th style={{ width: 120 }}>Status</th>
                  <th className="ems-num" style={{ width: 96 }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {slice.map((o) => {
                  const isActive = String(o.organization_id) === String(activeOrgId);
                  const toggleDisabled = isSingleOrg || !canSwitchOrg;
                  return (
                    <tr key={o.organization_id}>
                      <td>
                        <div className="ems-act-cell">
                          <span className="ems-org-mark">
                            {orgInitials(
                              o.organization_short_name,
                              o.organization_name
                            )}
                          </span>
                          <span className="ems-lead-name">{o.organization_name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="ems-org-short">
                          {o.organization_short_name || "—"}
                        </span>
                      </td>
                      <td>
                        <span className="ems-org-type">{getOrgType(o)}</span>
                      </td>
                      <td className="ems-num">
                        <span className="ems-batch-stu">{getEntityCount(o)}</span>
                      </td>
                      <td>
                        <OrgStatusToggle
                          active={isActive}
                          disabled={toggleDisabled}
                          onToggle={() => switchActiveOrg(o.organization_id)}
                        />
                      </td>
                      <td className="ems-num">
                        {canEdit ? (
                          <button
                            type="button"
                            className="ems-link-edit"
                            onClick={() =>
                              navigate(
                                `/organization/details/edit/${o.organization_id}`
                              )
                            }
                          >
                            Edit
                          </button>
                        ) : (
                          <span className="ems-rep-zero">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!slice.length && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: 44, color: "var(--ems-faint)" }}>
                      No organizations match your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="ems-pager">
            <span className="ems-pg-info">
              Showing <b>{slice.length}</b> of <b>{filtered.length}</b> organizations
            </span>
            <div className="ems-pg-spacer" />
            <div className="ems-rpp">
              Rows per page
              <select
                className="ems-select"
                value={rpp}
                onChange={(e) => {
                  setRpp(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <span className="ems-pg-page">
              Page <b>{curPage}</b> of <b>{pages}</b>
            </span>
            <div className="ems-pg-nav">
              <button
                type="button"
                className="ems-pg-btn"
                disabled={curPage <= 1}
                onClick={() => setPage(1)}
                aria-label="First page"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                type="button"
                className="ems-pg-btn"
                disabled={curPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </button>
              {pgList(curPage, pages).map((n, i) =>
                n === "…" ? (
                  <span key={`e-${i}`} className="ems-pg-ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={n}
                    type="button"
                    className={`ems-pg-num${n === curPage ? " active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                )
              )}
              <button
                type="button"
                className="ems-pg-btn"
                disabled={curPage >= pages}
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </button>
              <button
                type="button"
                className="ems-pg-btn"
                disabled={curPage >= pages}
                onClick={() => setPage(pages)}
                aria-label="Last page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrganizationAddInList;
